import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/locale-context';
import { copyLink } from '../lib/copy-link';
import { CheckIcon, CopyIcon } from './icons';

/**
 * «№ 17616038-1» in the lot header, copied with one tap (Kazakhstan, owner
 * 2026-10-21: lots are found by their platform number, and the page did not
 * show one where a reader looks first).
 *
 * The number is the announcement's own (`procedure.announcementNumber`) — the
 * PUBLIC one, already printed for every viewer as «Номер объявления» in
 * «Порядок проведения». The platform's lot number (`lotNumber`) stays Pro-only
 * in the identity line under the title; this chip is never handed it.
 *
 * A failed copy (an in-app browser that refuses the clipboard) says nothing:
 * the number is on screen and can be selected by hand.
 */
export function LotNumberChip({ number }: { number: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    [],
  );

  async function copy() {
    if (!(await copyLink(number))) return;
    setCopied(true);
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <span className="inline-flex max-w-full items-center">
      <button
        type="button"
        onClick={() => void copy()}
        data-testid="lot-number-chip"
        title={t('tender.number.copyLabel', { number })}
        aria-label={t('tender.number.copyLabel', { number })}
        className="inline-flex max-w-full items-center gap-1.5 rounded-pill border border-white/20 px-2.5 py-1 text-[12px] font-semibold text-white/85 transition-colors hover:bg-white/10"
      >
        <span className="tabular-nums [overflow-wrap:anywhere]">№ {number}</span>
        {copied ? (
          <CheckIcon aria-hidden="true" className="h-3 w-3 shrink-0" />
        ) : (
          <CopyIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-white/60" />
        )}
      </button>
      {/* The confirmation, shown and spoken — outside the button, whose own
          content a screen reader does not announce as it changes. */}
      <span
        role="status"
        aria-live="polite"
        className={copied ? 'ml-1.5 text-[12px] font-medium text-white/70' : 'sr-only'}
      >
        {copied ? t('tender.number.copied') : ''}
      </span>
    </span>
  );
}
